import type { OrgCron } from './types'
import type { CronJobSchedule } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { cronToSummaryPtBR, cronToSchedule } from '@/frontend/utils/scheduleCronHelpers'

// Rótulos de canal (mesmos da Auditoria por desfecho), reframados para a home.
const CHANNEL_LABELS: Record<string, string> = {
  chat: 'Chat',
  webchat: 'Webchat',
  whatsapp: 'WhatsApp',
  'whatsapp-cloud': 'WhatsApp',
  telegram: 'Telegram',
  discord: 'Discord',
  msteams: 'Teams',
  slack: 'Slack',
  cli: 'CLI',
  cron: 'Rotina',
  email: 'E-mail',
}

export function channelLabel(channel: string | null): string | null {
  if (!channel) return null
  return CHANNEL_LABELS[channel] ?? channel
}

// "há X min" para a janela ao vivo (sempre ≤15 min). Abaixo de 1 min → "agora".
export function minutesAgoLabel(iso: string | null): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const mins = Math.max(0, Math.round((Date.now() - then) / 60_000))
  if (mins < 1) return 'agora'
  return `há ${mins} min`
}

// Schedule legível de uma rotina cross-agente. Usa os mesmos helpers da aba
// Rotinas (cron.expr → resumo pt-BR; every → intervalo; at → uma vez).
export function scheduleLabel(cron: OrgCron): string {
  return scheduleSummaryLabel(cron.schedule)
}

// Variante primitiva (Iteração 3, Onda B): o painel "Agendado" do dashboard
// de operação lê o schedule direto do CronJob da Fleet, sem passar pelo
// shape agregado OrgCron.
export function scheduleSummaryLabel(schedule: CronJobSchedule | null | undefined): string {
  if (!schedule) return 'Sem agenda'
  if (schedule.kind === 'cron' && schedule.expr) return cronToSummaryPtBR(schedule.expr)
  if (schedule.kind === 'every' && schedule.everyMs) return intervalLabel(schedule.everyMs)
  if (schedule.kind === 'at' && schedule.at) {
    return `Uma vez em ${shortDateTime(Date.parse(schedule.at))}`
  }
  return schedule.kind
}

function intervalLabel(ms: number): string {
  const minutes = Math.round(ms / 60_000)
  if (minutes < 60) return `A cada ${minutes} min`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `A cada ${hours}h`
  const days = Math.round(hours / 24)
  return `A cada ${days}d`
}

// "Quando" da próxima execução: dia relativo (Hoje/Amanhã/dd mmm) + hora.
export function nextRunLabel(cron: OrgCron): string | null {
  return nextRunLabelFromMs(cron.next_run_at_ms)
}

// Variante primitiva para o CronJob da Fleet (state.nextRunAtMs).
export function nextRunLabelFromMs(ms: number | null | undefined): string | null {
  if (!ms) return null
  const date = new Date(ms)
  if (Number.isNaN(date.getTime())) return null

  const now = new Date()
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const dayDelta = Math.round((startOfDay(date) - startOfDay(now)) / 86_400_000)

  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  let day: string
  if (dayDelta === 0) day = 'Hoje'
  else if (dayDelta === 1) day = 'Amanhã'
  else if (dayDelta > 1 && dayDelta < 7) {
    day = date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')
  } else {
    day = date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '')
  }
  return `${day} ${time}`
}

// Hora curta relativa de um artefato (Hoje HH:MM / Ontem / dd mmm).
export function artifactTimeLabel(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const now = new Date()
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const dayDelta = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000)
  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  if (dayDelta === 0) return `Hoje ${time}`
  if (dayDelta === 1) return `Ontem ${time}`
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '')
}

function shortDateTime(ms: number): string {
  return new Date(ms).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Time-of-day de um cron para exibir separadamente quando útil (não usado na
// listagem simples, mas exportado para reuso — mesma origem da aba Rotinas).
export function timeOfDayLabel(cron: OrgCron): string | null {
  const schedule = cron.schedule
  if (schedule?.kind === 'cron' && schedule.expr) return cronToSchedule(schedule.expr)?.timeOfDay ?? null
  return null
}
